export const ANONYMOUS_MODE_KEY = 'sf_anonymous_mode';

const FIRST_NAMES = [
  'Lars',
  'Milan',
  'Koen',
  'Eva',
  'Noor',
  'Thijs',
  'Lisa',
  'Daan',
  'Sara',
  'Bram',
  'Marit',
  'Yasmin',
];

const LAST_NAMES = [
  'Vermeer',
  'de Boer',
  'Veldhuis',
  'Hartman',
  'Alami',
  'Koster',
  'Hofstra',
  'Veldman',
  'Benali',
  'Nijenhuis',
  'Scholten',
  'Haddad',
];

const COMPANIES = [
  'Helderdak',
  'Noordlicht',
  'Dakstroom',
  'Zonlijn',
  'HelioWerk',
  'Lumen Dak',
  'Sterlicht',
  'Dakraam Groep',
];

const STREETS = [
  'Lindenlaan',
  'Kerkstraat',
  'Molenweg',
  'Beukenlaan',
  'Stationsweg',
  'Dorpsstraat',
  'Eikenlaan',
  'Havenkade',
];

const CITIES = [
  'Utrecht',
  'Amersfoort',
  'Zwolle',
  'Deventer',
  'Haarlem',
  'Arnhem',
  'Breda',
  'Groningen',
];

const EMAIL_KEYS = new Set([
  'email',
  'customer_email',
  'contact_email',
  'user_email',
  'invoicing_email',
  'billing_email',
  'invoice_email',
]);

const PHONE_KEYS = new Set([
  'phone',
  'phone_number',
  'mobile',
  'telephone',
  'customer_phone',
  'contact_phone',
  'phonenumber',
]);

const FIRST_NAME_KEYS = new Set(['first_name', 'firstname']);
const LAST_NAME_KEYS = new Set(['last_name', 'lastname']);
const FULL_NAME_KEYS = new Set(['full_name', 'display_name', 'contact_name', 'customer_name']);
const COMPANY_KEYS = new Set(['company_name', 'organisation_name', 'organization_name']);
const CUSTOMER_STRING_KEYS = new Set(['customer', 'contact', 'who']);
const CITY_KEYS = new Set(['city', 'plaats']);
const POSTAL_KEYS = new Set(['postal_code', 'zipcode', 'zip', 'postcode']);
const STREET_KEYS = new Set([
  'address',
  'street',
  'street_name',
  'street_address',
  'address_line_1',
  'address_line1',
  'address_line_2',
  'address_line2',
  'house_number',
  'installation_address',
  'location_address',
]);
const REGISTRY_KEYS = new Set([
  'iban',
  'kvk',
  'kvk_number',
  'btw',
  'vat_number',
  'coc_number',
  'bank_account',
  'bic',
]);
const AVATAR_KEYS = new Set(['avatar_url', 'avatar', 'profile_image', 'photo_url']);
const PERSON_HINTS = new Set([
  'email',
  'phone',
  'phone_number',
  'first_name',
  'last_name',
  'customer_type',
  'brand_settings',
  'avatar_url',
  'user_metadata',
  'company_name',
  'kvk',
  'kvk_number',
  'role',
]);

function hashString(input) {
  const str = String(input ?? '');
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function pick(seed, list) {
  return list[hashString(seed) % list.length];
}

function looksLikeCompanyName(value) {
  return /\b(bv|b\.v\.|nv|vof|cv|holding|groep|install|techniek|solar|dak)\b/i.test(value);
}

export function fakePerson(seed) {
  const source = seed || 'anon';
  const first = pick(`${source}:first`, FIRST_NAMES);
  const last = pick(`${source}:last`, LAST_NAMES);
  return {
    first,
    last,
    full: `${first} ${last}`,
    initials: `${first.charAt(0)}${last.charAt(0)}`.toUpperCase(),
  };
}

export function fakeCompany(seed) {
  return pick(seed || 'company', COMPANIES);
}

export function fakeEmail(seed) {
  const person = fakePerson(seed || 'mail');
  const company = fakeCompany(seed || 'mail').toLowerCase().replace(/\s+/g, '');
  return `${person.first.toLowerCase()}@${company}.nl`;
}

function fakePhone(seed) {
  const n = hashString(seed || 'phone');
  const rest = String(10000000 + (n % 90000000)).slice(0, 8);
  return `06 ${rest.slice(0, 2)} ${rest.slice(2, 4)} ${rest.slice(4, 6)} ${rest.slice(6, 8)}`;
}

function fakeStreet(seed) {
  const number = (hashString(seed) % 180) + 1;
  return `${pick(seed, STREETS)} ${number}`;
}

function fakeCity(seed) {
  return pick(seed || 'city', CITIES);
}

function fakePostal(seed) {
  const n = hashString(seed || 'zip');
  const digits = 1000 + (n % 9000);
  const letters = String.fromCharCode(65 + (n % 26), 65 + ((n >> 5) % 26));
  return `${digits} ${letters}`;
}

function fakeIban(seed) {
  const n = String(hashString(seed || 'iban')).padStart(10, '0');
  return `NL${n.slice(0, 2)} TEST 0${n.slice(2, 9)} 12`;
}

function looksLikePersonOrOrg(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  return Object.keys(obj).some((key) => PERSON_HINTS.has(key));
}

function shouldSkipKey(key) {
  const lower = String(key).toLowerCase();
  return (
    lower === 'id' ||
    lower.endsWith('_id') ||
    lower.includes('token') ||
    lower.includes('secret') ||
    lower.includes('password') ||
    lower.includes('hash') ||
    lower === 'status' ||
    lower === 'type' ||
    lower === 'html' ||
    lower === 'css' ||
    lower === 'svg'
  );
}

function anonymizeString(key, value, parent) {
  const lower = String(key).toLowerCase();

  if (AVATAR_KEYS.has(lower)) return null;
  if (lower === 'icon' && (parent?.primary_color || parent?.primary_accent_color)) return null;

  if (EMAIL_KEYS.has(lower)) return fakeEmail(value);
  if (PHONE_KEYS.has(lower)) return fakePhone(value);
  if (FIRST_NAME_KEYS.has(lower)) return fakePerson(value).first;
  if (LAST_NAME_KEYS.has(lower)) return fakePerson(value).last;
  if (FULL_NAME_KEYS.has(lower) || COMPANY_KEYS.has(lower)) {
    return looksLikeCompanyName(value) ? fakeCompany(value) : fakePerson(value).full;
  }
  if (CUSTOMER_STRING_KEYS.has(lower)) return fakePerson(value).full;
  if (CITY_KEYS.has(lower)) return fakeCity(value);
  if (POSTAL_KEYS.has(lower)) return fakePostal(value);
  if (STREET_KEYS.has(lower)) return fakeStreet(value);
  if (REGISTRY_KEYS.has(lower)) return fakeIban(value);

  if (lower === 'name' && looksLikePersonOrOrg(parent)) {
    if (parent.brand_settings || looksLikeCompanyName(value)) {
      return fakeCompany(value);
    }
    return fakePerson(value).full;
  }

  return value;
}

function walk(value, key, parent, seen) {
  if (value == null) return value;
  if (typeof value === 'string') {
    if (!key || shouldSkipKey(key)) return value;
    return anonymizeString(key, value, parent);
  }
  if (typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => walk(item, key, parent, seen));
  }

  const next = {};
  const titleSources = [];
  if (typeof value.title === 'string' && value.lead && typeof value.lead === 'object') {
    ['name', 'company_name', 'last_name', 'first_name'].forEach((field) => {
      if (typeof value.lead[field] === 'string' && value.lead[field]) {
        titleSources.push(value.lead[field]);
      }
    });
  }

  Object.keys(value).forEach((childKey) => {
    if (childKey === 'brand_settings' && value[childKey] && typeof value[childKey] === 'object') {
      next[childKey] = {
        ...walk(value[childKey], childKey, value, seen),
        icon: null,
        logo: null,
      };
      return;
    }
    next[childKey] = walk(value[childKey], childKey, value, seen);
  });

  if (titleSources.length && typeof next.title === 'string') {
    titleSources
      .sort((a, b) => b.length - a.length)
      .forEach((from) => {
        if (!next.title.includes(from)) return;
        const replacement = looksLikeCompanyName(from)
          ? fakeCompany(from)
          : fakePerson(from).full;
        next.title = next.title.split(from).join(replacement);
      });
  }

  return next;
}

export function isAnonymousMode() {
  try {
    return localStorage.getItem(ANONYMOUS_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

export function applyAnonymousDataset() {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.anonymous = isAnonymousMode() ? 'true' : 'false';
}

export function setAnonymousMode(enabled, { reload = false } = {}) {
  try {
    localStorage.setItem(ANONYMOUS_MODE_KEY, enabled ? '1' : '0');
  } catch {
    // Private mode: in-memory only for this page.
  }
  applyAnonymousDataset();
  if (reload && typeof window !== 'undefined') {
    window.location.reload();
  }
}

export function anonymizeData(data) {
  if (data == null || typeof data !== 'object') return data;
  if (data instanceof Date) return data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return data;
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) return data;
  return walk(data, null, null, new WeakSet());
}

export function maskAuthUser(user) {
  if (!user) return user;
  const seed = user.user_metadata?.first_name || user.email || user.id;
  const person = fakePerson(seed);
  return {
    ...user,
    email: user.email ? fakeEmail(user.email) : user.email,
    user_metadata: {
      ...(user.user_metadata || {}),
      first_name: person.first,
      last_name: person.last,
      full_name: person.full,
      name: person.full,
      email: user.email ? fakeEmail(user.email) : user.user_metadata?.email,
      avatar_url: null,
    },
  };
}

if (typeof document !== 'undefined') {
  applyAnonymousDataset();
}
